import React, { FC, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import FilterWrapper from "Components/FilterWrapper/FilterWrapper";
import ModalOptions from "Components/UI/ModalFilters/ModalOptions";
import { ICurrentVehicle } from "Shared/Types/vehicle";
import DateRange from "Components/UI/DateRange/DateRange";
import CalendarTable from "Components/Dashboard/CalendarTable";
import Block from "Components/UI/Block/Block";
import { useAppDispatch } from "Redux/store";
import { selectVehicleList } from "Redux/Vehicle/slice";
import { getVehicleList } from "Redux/Vehicle/reducer";
import { LIMIT_VEHICLES } from "Shared/Constants/app";
import {
  selectDashboardIssuesCalendarList,
  selectDashboardIssuesCalendarLoading,
} from "Redux/Dashboard/slice";
import { getDashboardIssuesCalendar } from "Redux/Dashboard/reducer";
import {
  getDateCalendarTableHeads,
  getDaysBetweenDates,
} from "Shared/Utils/dashboard";
import { getIssue } from "Redux/Issue/reducer";

import styles from "../styles.module.scss";
import { useIssuesCalendar } from "./useIssuesCalendar";
import { useCommonDashboardHook } from "../useCommonDashboardHook";
import SkeletonLoader from "Components/SkeletonLoader/SkeletonLoader";

type Props = {
  openDetailAssigment: () => void;
  closeDetailAssigment: () => void;
  issueId: string | null;
  setIssueId: (id: string | null) => void;
};

const IssuesCalendar: FC<Props> = ({
  openDetailAssigment,
  setIssueId,
  issueId,
}) => {
  const dispatch = useAppDispatch();
  const vehicles = useSelector(selectVehicleList) || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const isYearCalendar = searchParams.has("year");
  const issuesCalendarListLoading = useSelector(
    selectDashboardIssuesCalendarLoading
  );
  const issuesCalendarList = useSelector(selectDashboardIssuesCalendarList);

  const fetchVehicle = (search: string) => {
    return dispatch(
      getVehicleList({ search, limit: LIMIT_VEHICLES, offset: 0 })
    );
  };

  const { getDateLogicCircle, params } = useIssuesCalendar();
  const { startDate, endDate } = useCommonDashboardHook();

  useEffect(() => {
    if (issueId) {
      dispatch(getIssue(issueId));
      openDetailAssigment();
    }
  }, [issueId]);

  const issuesToRangeParam = searchParams.get("issues_to");
  const issuesFromRangeParam = searchParams.get("issues_from");
  const issuesVehicleParam = searchParams.get("issues_vehicle");

  useEffect(() => {
    dispatch(getDashboardIssuesCalendar(params));
  }, [issuesToRangeParam, issuesFromRangeParam, issuesVehicleParam]);

  if (issuesCalendarListLoading) return <SkeletonLoader height="410px" />;

  return (
    <Block className={styles.container}>
      <h4 className={styles.container__title}>Issues</h4>
      <FilterWrapper>
        <ModalOptions<ICurrentVehicle>
          options={vehicles}
          fetchOptions={fetchVehicle}
          type="issues_vehicle"
          title="Vehicle"
          zIndex
        />
        <DateRange
          dateRangeName="issues_date_range"
          rangeStartName="issues_from"
          rangeEndName="issues_to"
        />
      </FilterWrapper>
      {issuesCalendarList.length ? (
        <CalendarTable
          renderCellMonth={getDateLogicCircle}
          renderCellDate={getDateLogicCircle}
          onItemClick={setIssueId}
          isYearCalendar={isYearCalendar}
          dateColumns={getDateCalendarTableHeads(
            getDaysBetweenDates(startDate("issues_from"), endDate("issues_to"))
          )}
          data={issuesCalendarList}
        />
      ) : (
        <p className={styles.container__emptyText}>Issues not found</p>
      )}
    </Block>
  );
};

export default IssuesCalendar;
